import React from "react";
import { LegalLayout, H2, P, UL } from "./LegalLayout";

export default function SubscriptionTerms() {
  return (
    <LegalLayout
      testid="legal-subscription"
      eyebrow="✦ THE BLOOM CLUB BY HERITAGE DAHLIAS ✦"
      title="Subscription Terms & Conditions"
    >
      <H2 n="1">Bloom Club Membership</H2>
      <P>
        The Heritage Bloom Club is a seasonal flower subscription service connecting customers
        with local flower growers.
      </P>
      <P>
        Subscribers receive recurring deliveries of seasonal flowers grown and supplied by
        participating Bloom Club growers within their region.
      </P>
      <P>
        By purchasing a Bloom Club subscription, customers agree to these Terms &amp; Conditions.
      </P>

      <H2 n="2">Seasonal Product Disclaimer</H2>
      <P>Bloom Club flowers are a seasonal agricultural product.</P>
      <P>
        Flower varieties, colours, stem counts and bloom composition will vary throughout the
        season based on growing conditions and availability.
      </P>
      <P>No two Bloom Boxes are identical.</P>
      <P>
        Customers acknowledge that seasonal variation forms part of the Bloom Club experience and
        does not constitute a product defect.
      </P>

      <H2 n="3">Subscription Inclusions</H2>
      <P>Bloom Club subscriptions may include:</P>
      <UL>
        <li>Weekly deliveries</li>
        <li>Fortnightly deliveries</li>
        <li>Monthly deliveries</li>
        <li>Seasonal memberships</li>
        <li>Special promotional offerings</li>
      </UL>
      <P>Subscription details will be outlined during the checkout process.</P>

      <H2 n="3a">Pre-Launch Waitlist (Expression Of Interest)</H2>
      <P>
        Prior to subscriptions opening, customers may register an Expression of Interest through
        the Heritage Bloom Club waitlist. Registering on the waitlist:
      </P>
      <UL>
        <li>Does not constitute a purchase, subscription or binding agreement</li>
        <li>Does not require any payment</li>
        <li>Holds your details solely to notify you when boxes open in your region</li>
        <li>May be removed at any time on request</li>
      </UL>
      <P>
        Waitlist information is stored under the same protections set out in Section 14 (Privacy)
        and Section 18 (Subscriber Protection) of these Terms.
      </P>

      <H2 n="4">Subscription Billing</H2>
      <P>
        Subscription payments are processed automatically using the payment method provided by
        the customer.
      </P>
      <P>
        Customers are responsible for ensuring payment information remains current and valid.
      </P>
      <P>
        Failure to process payment may result in delayed deliveries, suspension or cancellation
        of the subscription.
      </P>

      <H2 n="5">Subscription Changes</H2>
      <P>Customers may manage their subscription through the Subscriber Portal.</P>
      <P>Depending on the subscription type, customers may be able to:</P>
      <UL>
        <li>Update delivery addresses</li>
        <li>Pause future deliveries</li>
        <li>Resume subscriptions</li>
        <li>Update payment details</li>
      </UL>
      <P>Changes must be submitted prior to the published cut-off period for the next delivery.</P>

      <H2 n="6">Delivery Completion</H2>
      <P>
        A delivery is considered complete once flowers have been delivered to the nominated
        address and left in accordance with customer instructions.
      </P>
      <P>Ownership and responsibility transfer to the customer upon successful delivery.</P>

      <H2 n="7">Authority To Leave Deliveries</H2>
      <P>
        Customers authorise Bloom Club deliveries to be left in a safe location when no recipient
        is present.
      </P>
      <P>Where practical, deliveries will be left:</P>
      <UL>
        <li>Out of direct sunlight</li>
        <li>Protected from weather</li>
        <li>In accordance with delivery instructions</li>
      </UL>
      <P>
        Customers acknowledge that flowers are perishable products and should be unpacked
        promptly following delivery.
      </P>

      <H2 n="8">Incorrect Address Information</H2>
      <P>Customers are responsible for ensuring delivery information is accurate.</P>
      <P>The Heritage Bloom Club is not responsible for failed deliveries resulting from:</P>
      <UL>
        <li>Incorrect addresses</li>
        <li>Incomplete delivery information</li>
        <li>Restricted property access</li>
        <li>Failure to update address details</li>
      </UL>
      <P>Additional delivery fees may apply where re-delivery is required.</P>

      <H2 n="9">Flower Care &amp; Longevity</H2>
      <P>Flowers are a fresh agricultural product.</P>
      <P>Flower longevity varies based on:</P>
      <UL>
        <li>Variety</li>
        <li>Season</li>
        <li>Environmental conditions</li>
        <li>Customer care practices</li>
      </UL>
      <P>The Heritage Bloom Club does not guarantee a specific vase life.</P>
      <P>Customers are encouraged to follow all supplied flower care instructions.</P>

      <H2 n="10">Replacement &amp; Resolution Policy</H2>
      <P>
        Due to the perishable nature of flowers, subscriptions are generally non-refundable once
        flowers have been harvested, prepared or dispatched.
      </P>
      <P>
        Where flowers arrive in poor condition due to circumstances reasonably within our
        control, Heritage may:
      </P>
      <UL>
        <li>Arrange replacement flowers</li>
        <li>Provide a replacement delivery</li>
        <li>Issue Bloom Club credit</li>
        <li>Offer another reasonable resolution</li>
      </UL>
      <P>Our objective is always to resolve genuine quality concerns fairly and promptly.</P>

      <H2 n="11">No Refunds For Change Of Mind</H2>
      <P>Refunds will not generally be provided for:</P>
      <UL>
        <li>Change of mind</li>
        <li>Seasonal flower variations</li>
        <li>Colour preferences</li>
        <li>Variety preferences</li>
        <li>Customer absence at delivery</li>
        <li>Failure to follow care instructions</li>
        <li>Incorrect delivery information supplied by the customer</li>
      </UL>

      <H2 n="12">Crop Failure &amp; Seasonal Events</H2>
      <P>Customers acknowledge that flower farming is subject to seasonal and agricultural risks.</P>
      <P>These may include:</P>
      <UL>
        <li>Frost</li>
        <li>Flooding</li>
        <li>Drought</li>
        <li>Pests</li>
        <li>Disease</li>
        <li>Crop failure</li>
        <li>Natural disasters</li>
      </UL>
      <P>Where necessary, Heritage may:</P>
      <UL>
        <li>Substitute flowers</li>
        <li>Modify Bloom Box contents</li>
        <li>Source flowers from another participating grower</li>
        <li>Delay deliveries</li>
        <li>Offer alternative resolutions</li>
      </UL>

      <H2 n="13">Force Majeure</H2>
      <P>
        Heritage Bloom Club shall not be liable for delays, interruptions or service changes
        resulting from events beyond reasonable control, including severe weather, crop failure,
        transportation disruptions, natural disasters, biosecurity events or government
        restrictions.
      </P>

      <H2 n="14">Privacy &amp; Customer Information</H2>
      <P>Customer information is collected for the purpose of:</P>
      <UL>
        <li>Managing subscriptions</li>
        <li>Processing payments</li>
        <li>Coordinating deliveries</li>
        <li>Providing customer support</li>
        <li>Sending Bloom Club communications</li>
      </UL>
      <P>Customer information will not be sold to third parties.</P>
      <P>Information may be shared with participating growers where required to fulfil deliveries.</P>

      <H2 n="15">Marketing Communications</H2>
      <P>Customers may receive communications relating to:</P>
      <UL>
        <li>Deliveries</li>
        <li>Subscription updates</li>
        <li>Seasonal launches</li>
        <li>Promotions</li>
        <li>Grower stories</li>
      </UL>
      <P>Customers may unsubscribe from marketing communications at any time.</P>

      <H2 n="16">Limitation Of Liability</H2>
      <P>
        To the maximum extent permitted by law, Heritage Bloom Club shall not be liable for loss,
        theft, damage or deterioration occurring after successful delivery.
      </P>
      <P>This includes:</P>
      <UL>
        <li>Theft after delivery</li>
        <li>Animal damage</li>
        <li>Weather exposure</li>
        <li>Delayed collection</li>
        <li>Customer handling or storage</li>
      </UL>
      <P>Nothing in these terms limits rights available under applicable consumer protection laws.</P>

      <H2 n="17">Our Commitment</H2>
      <P>
        The Heritage Bloom Club exists to connect local growers with local communities through
        seasonal flowers and exceptional customer experiences.
      </P>
      <P>
        We are committed to operating fairly, communicating openly and delivering a Bloom Club
        experience customers look forward to receiving each season.
      </P>

      <H2 n="18">Subscriber Protection</H2>
      <P>
        The Heritage Bloom Club is committed to protecting every subscriber against unfair
        treatment, misrepresentation and misuse of their information. As a subscriber you are
        protected by the following commitments:
      </P>
      <UL>
        <li>Your personal information, address and contact details will never be sold, traded or licensed to any third party</li>
        <li>Your details may only be shared with your allocated grower for the sole purpose of fulfilling your subscription</li>
        <li>You may withdraw your information from Bloom Club systems at any time on written request</li>
        <li>You will receive plain-language confirmation of any subscription, change or cancellation</li>
        <li>You will not be charged outside of clearly disclosed subscription pricing</li>
        <li>You retain all rights available under the Consumer Guarantees Act 1993 and the Fair Trading Act 1986 of Aotearoa New Zealand</li>
        <li>Any complaint will receive a written response within seven (7) business days</li>
      </UL>

      <H2 n="19">Intellectual Property Protection</H2>
      <P>
        All Heritage Bloom Club brand assets, including logos, packaging design, written
        content, photographs, illustrations, the curated stained-glass artwork, the website,
        software platforms, subscriber portal, grower portal and seasonal bloom-box concept, are
        the intellectual property of Heritage Dahlias NZ Limited.
      </P>
      <P>
        These assets are protected by copyright, trade-mark and unregistered design rights under
        the laws of Aotearoa New Zealand and international treaty. Subscribers and visitors are
        granted a limited, non-exclusive, non-transferable licence to view this content for
        personal, non-commercial use only.
      </P>
      <P>
        Subscribers acknowledge that their participation in the Bloom Club does not transfer any
        intellectual property rights to them. Bloom Box arrangements, accompanying creative
        guides and seasonal photographic releases remain the property of Heritage Dahlias NZ
        Limited.
      </P>

      <H2 n="20">Grower Protection (As It Relates To Subscribers)</H2>
      <P>
        Subscribers acknowledge that their box is hand-grown and delivered by an independent
        flower farm operating under the Heritage Bloom Club framework. Subscribers agree:
      </P>
      <UL>
        <li>Not to solicit, recruit or contract directly with a Bloom Club grower outside of the Heritage Bloom Club platform during the subscription term and for twelve (12) months thereafter</li>
        <li>Not to redistribute, resell or commercially exploit the contents of any Bloom Box</li>
        <li>To respect the privacy of growers and treat them with courtesy in all communications</li>
        <li>To raise any concerns through Heritage so they can be resolved fairly</li>
      </UL>

      <H2 n="21">Insurance Coverage</H2>
      <P>
        Heritage Dahlias NZ Limited maintains commercial general liability insurance covering
        the operation of the Heritage Bloom Club platform.
      </P>
      <P>This coverage is intended to address:</P>
      <UL>
        <li>Public liability arising from Bloom Club activities</li>
        <li>Product liability associated with seasonal floral products supplied through the Bloom Club</li>
        <li>Loss, theft or damage to Bloom Club packaging during transit while in Heritage care</li>
      </UL>
      <P>
        All participating growers are required to carry their own appropriate insurance covering
        their growing, packing and delivery operations as set out in the Grower Terms.
      </P>
      <P>
        Insurance coverage does not extend to losses arising from misuse, negligence after
        delivery, or events expressly excluded under Section 13 (Force Majeure) of these Terms.
        Subscribers may request a current certificate of insurance summary by writing to
        heritagedahliasnz@gmail.com.
      </P>
    </LegalLayout>
  );
}
